import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';

import type { LeadObject, LeadObjectInput } from '../api/types';
import { useDialog } from '../components/dialogContext';
import { MutationError } from '../components/MutationError';
import { propertyLine, propertyTypeLabels } from '../owners/labels';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { filled, objectFacts, registerFields, unitFields, type ObjectField } from './labels';

// LeadObjectsPanel: Objekte des Leads (OFFENE_FRAGEN B-20) – das Hauptobjekt mit Anschrift und Merkmalen wie am Lead,
// dazu Einheit laut Teilungserklärung und Grundbuch, die es nur am Objekt gibt (hier ergänzen). Ein Lead mit mehreren
// Objekten entsteht, wenn derselben Person bzw. demselben Haushalt mehrere Objekte im selben Haus gehören; wird eine
// Einheit getrennt verkauft, „Als eigenen Lead führen“. compact: Akquise-Karte (nur bei mehreren Objekten, ohne Bearbeiten).
export function LeadObjectsPanel({ leadId, compact = false }: { leadId: string; compact?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const dialog = useDialog();
  const objects = useQuery({ queryKey: projectKeys.leadObjects(project.id, leadId), queryFn: ({ signal }) => papi.leadObjects(leadId, signal) });
  const [editing, setEditing] = useState<string | null>(null);
  const split = useMutation({
    mutationFn: (objectId: string) => papi.splitLeadObject(leadId, objectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.leadObjects(project.id, leadId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.leadOwners(project.id, leadId) });
    },
  });
  const items = objects.data?.items ?? [];
  if (items.length === 0 || (compact && items.length < 2)) return null;
  if (compact) {
    return (
      <div className="owner-properties lead-objects-compact">
        <p className="muted small">Dieser Lead umfasst {items.length} Objekte – im Gespräch alle ansprechen:</p>
        <ul className="plain-list">
          {items.map((o) => (
            <li key={o.id}>
              {propertyLine(o)} <span className="badge badge-muted">{propertyTypeLabels[o.property_type] ?? 'Art offen'}</span>
              {o.living_area_sqm ? <span className="muted"> · {o.living_area_sqm.toLocaleString('de-DE')} m²</span> : null}
              {o.unit_number && <span className="muted"> · {o.unit_number}</span>}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <section className="card lead-objects" aria-label="Objekte">
      <h2>{items.length > 1 ? `Objekte (${items.length})` : 'Objekt'}</h2>
      <ul className="plain-list">
        {items.map((o) => (
          <li key={o.id} className="lead-object">
            <p>
              <strong>{propertyLine(o)}</strong> <span className="badge badge-muted">{propertyTypeLabels[o.property_type] ?? 'Art offen'}</span>
              {items.length > 1 && o.is_main && <span className="badge badge-muted">Hauptobjekt</span>}
            </p>
            <Facts pairs={objectFacts(o)} />
            {editing === o.id ? (
              <ObjectForm leadId={leadId} object={o} onDone={() => setEditing(null)} />
            ) : (
              <>
                <Facts title="Einheit" pairs={filled(o, unitFields)} />
                <Facts title="Grundbuch und Kataster" pairs={filled(o, registerFields)} />
                {o.notes && <p className="muted">{o.notes}</p>}
                {can('lead.edit') && (
                  <div className="form-actions">
                    <button type="button" className="btn btn-small" onClick={() => setEditing(o.id)}>
                      Einheit und Grundbuch {filled(o, [...unitFields, ...registerFields]).length > 0 ? 'ändern' : 'ergänzen'}
                    </button>
                    {!o.is_main && o.source_lead_id && o.source_lead_id !== leadId && (
                      <button
                        type="button"
                        className="btn btn-small btn-ghost"
                        disabled={split.isPending}
                        onClick={async () => {
                          const ok = await dialog.confirm({
                            title: 'Als eigenen Lead führen?',
                            message: `${propertyLine(o)} wird wieder ein eigener Lead – etwa weil die Einheit getrennt verkauft wird. Das System legt ihn danach nicht mehr mit diesem Lead zusammen.`,
                            confirmLabel: 'Als eigenen Lead führen',
                          });
                          if (ok) split.mutate(o.id);
                        }}
                      >
                        Als eigenen Lead führen
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      {split.isError && <MutationError error={split.error} />}
      {split.isSuccess && (
        <p className="alert alert-success" role="status">
          Eigener Lead angelegt: <Link to={`/p/${project.key}/leads/${split.data.lead_id}`}>öffnen</Link>
        </p>
      )}
      {items.some((o) => o.is_main) && <p className="muted small">Anschrift und Merkmale des Hauptobjekts werden am Lead geändert.</p>}
    </section>
  );
}

function Facts({ title, pairs }: { title?: string; pairs: [string, string][] }) {
  if (pairs.length === 0) return null;
  return (
    <>
      {title && <p className="muted small">{title}</p>}
      <dl className="facts">
        {pairs.map(([k, v]) => (
          <Fragment key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </Fragment>
        ))}
      </dl>
    </>
  );
}

function ObjectForm({ leadId, object, onDone }: { leadId: string; object: LeadObject; onDone: () => void }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const all = [...unitFields, ...registerFields];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries([...all.map(([k]) => [k, (object[k] as string | null) ?? '']), ['notes', object.notes]]),
  );
  const save = useMutation({
    mutationFn: () => papi.updateLeadObject(leadId, object.id, values as LeadObjectInput),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.leadObjects(project.id, leadId) });
      onDone();
    },
  });
  const field = ([k, label, hint]: [ObjectField, string, string]) => (
    <label key={k} className="field">
      <span>{label}</span>
      <input value={values[k] ?? ''} placeholder={hint} maxLength={200} onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))} />
    </label>
  );
  return (
    <form
      className="inline-panel"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <p className="muted small">Einheit laut Teilungserklärung</p>
      <div className="form-grid">{unitFields.map(field)}</div>
      <p className="muted small">Grundbuch und Kataster</p>
      <div className="form-grid">{registerFields.map(field)}</div>
      <label className="field wide">
        <span>Notiz zum Objekt</span>
        <textarea value={values.notes ?? ''} maxLength={4000} onChange={(e) => setValues((v) => ({ ...v, notes: e.target.value }))} />
      </label>
      {save.isError && <MutationError error={save.error} />}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={save.isPending}>
          Speichern
        </button>
        <button type="button" className="btn btn-ghost" onClick={onDone}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}
